import { msg } from '@lingui/core/macro';
import * as Sentry from '@sentry/tanstackstart-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { isIOSApp } from '#features/api/utils/appCheck';
import { i18n } from '#features/i18n/i18n';
import { toaster } from '#features/ui/components/Toaster';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';

export const useReCaptcha = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  /**
   * Кладёт свежий токен в localStorage, откуда его берёт `gqlFetcher` заголовком `tm-captcha`.
   *
   * Возвращает `false`, если токена нет — тогда отправлять запрос бессмысленно. Токен одноразовый
   * и живёт две минуты, а `executeRecaptcha` отсутствует, пока скрипт Google не загрузился: раньше
   * такой вызов молча проглатывался optional chaining'ом, в localStorage оставался **прошлый,
   * уже сожжённый** токен, и уходил именно он. Google отвечал `timeout-or-duplicate`, бэкенд —
   * `captcha.error`, а человек видел кнопку, которая просто ничего не делает, и жал её снова —
   * с тем же мёртвым токеном.
   */
  const setReCaptchaToken = async (actionName: string): Promise<boolean> => {
    // The gateway verifies App Check on iOS, including dev. gqlFetcher obtains the token;
    // an unavailable/invalid token is rejected by the server, never accepted as a dev bypass.
    if (isIOSApp()) {
      localStorage.deleteItem('reCaptchaToken');
      return true;
    }
    if (!envConfig.RECAPTCHA_KEY) return true;

    try {
      const token = await executeRecaptcha?.(actionName);

      if (!token) {
        throw new Error('recaptcha is not available');
      }

      localStorage.setItem('reCaptchaToken', token);
      return true;
    } catch (error) {
      // Сожжённый токен хуже отсутствующего: с ним бэкенд отвечает той же ошибкой, но уже после
      // похода в Google, и состояние залипает до перезагрузки страницы.
      localStorage.deleteItem('reCaptchaToken');

      // Единственное место, где этот отказ вообще можно посчитать: запрос не уходит, поэтому
      // счётчик `captcha_validation` в шлюзе его не видит по построению. Тег `scriptLoaded`
      // разделяет две причины, которые до сих пор путали между собой: скрипта нет вовсе
      // (блокировщик, VPN, недоступный google.com) против «скрипт загрузился, но сломан» —
      // наш перемонтаж провайдера либо зависший запрос к Google.
      Sentry.withScope((scope) => {
        scope.setTag('scope', 'captcha');
        scope.setTag('captcha.action', actionName);
        scope.setTag(
          'captcha.scriptLoaded',
          String(typeof window !== 'undefined' && 'grecaptcha' in window)
        );
        scope.setLevel('warning');
        scope.setContext('captcha', {
          action: actionName,
          reason: error instanceof Error ? error.message : String(error),
        });
        Sentry.captureMessage('captcha token not issued');
      });

      toaster.create({
        title: i18n._(
          msg({
            id: 'common:captcha.unavailable',
            message:
              'Could not verify that you are human. Reload the page and try again – if you use an ad blocker or a VPN, turn it off for this site.',
          })
        ),
        type: 'error',
      });

      return false;
    }
  };

  return { setReCaptchaToken };
};
